import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import ProfilePic from '../../components/profilepic/ProfilePic';
import AccountHoverCard from '../../components/profile/AccountHoverCard';
import MangaApiService from '../../services/MangaApiService';
import { getMangaErrorMessage, unwrapList } from './mangaStudioUtils';
import genStar from '../../assets/genStar.svg';
import trashIcon from '../../assets/ic_trash.svg';
import '../../components/ReviewComponent.css';

const MAX_LENGTH = 256;
const PAGE_SIZE = 6;
const STARS = Array.from({ length: 10 }, (_, index) => index + 1);
const LINK_PATTERN = /(https?:|www\.)/i;

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

// The server allows 10 reviewed titles per 5 hours and answers 429 with Retry-After in seconds.
const formatReviewLimit = (retryAfter) => {
    const seconds = Number(retryAfter);
    if (!Number.isFinite(seconds) || seconds <= 0) {
        return 'Слишком много отзывов подряд. Попробуйте позже.';
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.max(1, Math.ceil((seconds % 3600) / 60));
    const wait = hours > 0 ? `${hours} ч ${minutes} мин` : `${minutes} мин`;
    return `Можно оставить до 10 отзывов за 5 часов. Следующий — через ${wait}.`;
};

const formatDate = (value) => {
    const date = new Date(value);
    if (!value || Number.isNaN(date.getTime())) {
        return '';
    }
    return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

const pluralReviews = (count) => {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11) {
        return 'отзыв';
    }
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
        return 'отзыва';
    }
    return 'отзывов';
};

// Same card as ReviewComponent on anime pages; manga reviews carry only id and nick of the author.
export const MangaReviewCard = ({ review, canDelete, onDelete }) => {
    const navigate = useNavigate();
    const nickname = review.nick || 'Пользователь animix';
    const account = { id: review.userId, nickname };
    return (
        <div className="review">
            <div>
                <div className="review-con">
                    <div className="star-con">
                        <span className="star">★</span>
                        <span className="stars-count">{review.stars}</span>
                    </div>
                    <p className="review-date">{formatDate(review.updatedAt || review.createdAt)}</p>
                </div>
                {review.message ? <p className="review-message">{review.message}</p> : null}
                <div className="review-user-section">
                    <a
                        style={{ cursor: review.nick ? 'pointer' : 'default' }}
                        className="review-user-section"
                        onClick={() => {
                            if (review.nick) {
                                navigate(`/profile/${encodeURIComponent(review.nick)}`);
                            }
                        }}
                    >
                        <AccountHoverCard account={account}>
                            <ProfilePic isBig={false} account={account} />
                        </AccountHoverCard>
                        <p className="review-p">{nickname}</p>
                    </a>
                    {canDelete ? (
                        <img
                            style={{ height: 40, display: 'inline-block', paddingBottom: 5, verticalAlign: 'middle', cursor: 'pointer' }}
                            src={trashIcon}
                            alt="Удалить отзыв"
                            role="button"
                            tabIndex={0}
                            onClick={() => onDelete(review)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    onDelete(review);
                                }
                            }}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

MangaReviewCard.propTypes = {
    review: PropTypes.object.isRequired,
    canDelete: PropTypes.bool,
    onDelete: PropTypes.func,
};

MangaReviewCard.defaultProps = {
    canDelete: false,
    onDelete: () => {},
};

/** "Оценка пользователей" block of a manga page, built like the anime one. */
const MangaReviews = ({ manga, hasSession, isModerator, onRatingChange }) => {
    const mangaId = manga?.id;
    const isPublished = String(manga?.status || 'PUBLISHED').toUpperCase() === 'PUBLISHED';
    const viewerId = localStorage.getItem('profileId');

    const [reviews, setReviews] = useState([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [listError, setListError] = useState(null);
    const [ownReview, setOwnReview] = useState(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const [stars, setStars] = useState(5);
    const [hoveredStar, setHoveredStar] = useState(null);
    const [message, setMessage] = useState('');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);

    const loadPage = useCallback(async (nextPage) => {
        if (!mangaId) {
            return;
        }
        setListError(null);
        try {
            const response = await MangaApiService.getMangaReviews(mangaId, { page: nextPage, size: PAGE_SIZE });
            const items = unwrapList(response);
            setReviews((previous) => (nextPage === 0 ? items : [...previous, ...items]));
            setHasMore(response?.last === false);
            setPage(nextPage);
        } catch (err) {
            // Reviews are secondary: if they can't load, the block just stays empty.
            if (nextPage === 0) {
                setReviews([]);
            }
            setHasMore(false);
        }
    }, [mangaId]);

    useEffect(() => {
        loadPage(0);
    }, [loadPage]);

    useEffect(() => {
        if (!mangaId || !hasSession) {
            setOwnReview(null);
            return undefined;
        }
        let active = true;
        MangaApiService.getMyMangaReview(mangaId)
            .then((review) => active && setOwnReview(review))
            .catch(() => active && setOwnReview(null));
        return () => {
            active = false;
        };
    }, [mangaId, hasSession]);

    const openPopup = () => {
        setStars(ownReview?.stars || 5);
        setMessage(ownReview?.message || '');
        setFormError(null);
        setPopupOpen(true);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (LINK_PATTERN.test(message)) {
            setFormError('Ссылки в отзывах запрещены.');
            return;
        }
        setSaving(true);
        setFormError(null);
        try {
            const saved = await MangaApiService.saveMangaReview(mangaId, { stars, message: message.trim() });
            setOwnReview(saved);
            setPopupOpen(false);
            await loadPage(0);
            onRatingChange();
        } catch (err) {
            setFormError(getMangaErrorMessage(err, 'Не удалось сохранить отзыв.', {
                400: 'Отзыв не прошёл проверку: до 256 символов и без ссылок.',
                409: 'Отзывы можно оставлять только на опубликованные тайтлы.',
                429: formatReviewLimit(err?.response?.headers?.['retry-after']),
            }));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (review) => {
        if (!window.confirm('Удалить отзыв?')) {
            return;
        }
        try {
            await MangaApiService.deleteMangaReview(review.id);
            if (ownReview && String(ownReview.id) === String(review.id)) {
                setOwnReview(null);
            }
            setPopupOpen(false);
            await loadPage(0);
            onRatingChange();
        } catch (err) {
            setListError(getMangaErrorMessage(err, 'Не удалось удалить отзыв.'));
        }
    };

    const count = Number(manga?.reviewsCount || 0);
    const score = count > 0 && Number(manga?.avgRating) > 0 ? Number(manga.avgRating).toFixed(1) : null;
    const isOwn = (review) => (ownReview && String(review.id) === String(ownReview.id))
        || (viewerId && String(review.userId) === String(viewerId));

    return (
        <div className="manga-reviews">
            <h1 id="manga-reviews">Оценка пользователей</h1>
            {score ? (
                <div>
                    <div className="general-stars-con">
                        <img src={genStar} alt="" className="star-icon" />
                        <h1>{score}</h1>
                    </div>
                    <p className="settings-text-small">
                        Средняя оценка среди читателей animix · {count} {pluralReviews(count)}
                    </p>
                </div>
            ) : null}

            {hasSession && isPublished ? (
                <button type="button" className="ui-btn ui-btn--secondary ui-btn--block" onClick={openPopup}>
                    {ownReview ? 'Изменить отзыв' : 'Оставить отзыв'}
                </button>
            ) : null}
            {!hasSession ? <p className="settings-text-small">Войдите, чтобы оставить отзыв.</p> : null}

            {popupOpen ? (
                <div className="review-popup-overlay" onClick={() => setPopupOpen(false)}>
                    <div className="review-popup-content" onClick={(event) => event.stopPropagation()}>
                        <h2>Ваш отзыв</h2>
                        <div className="leave-review-container">
                            <p className="settings-text-small">Оставляя отзыв, Вы обязуетесь соблюдать Правила Сообщества</p>
                            <form onSubmit={handleSubmit}>
                                <div role="radiogroup" aria-label="Оценка от 1 до 10">
                                    {STARS.map((value) => (
                                        <span
                                            key={value}
                                            role="radio"
                                            aria-checked={stars === value}
                                            aria-label={`${value} из 10`}
                                            tabIndex={0}
                                            onClick={() => setStars(value)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    setStars(value);
                                                }
                                            }}
                                            onMouseEnter={() => setHoveredStar(value)}
                                            onMouseLeave={() => setHoveredStar(null)}
                                            className={stars >= value ? 'star-selected' : 'star-unselected'}
                                        >
                                            <span style={{ color: hoveredStar >= value ? '#7544B2' : '' }}>★</span>
                                        </span>
                                    ))}
                                </div>
                                <textarea
                                    value={message}
                                    style={{ height: 100, resize: 'none' }}
                                    onChange={(event) => setMessage(event.target.value)}
                                    maxLength={MAX_LENGTH}
                                    placeholder="Поделиться впечатлениями"
                                    rows="2"
                                />
                                {formError ? <p className="settings-text-small manga-review-error" role="alert">{formError}</p> : null}
                                <button className="ui-btn ui-btn--block" type="submit" disabled={saving}>
                                    {saving ? 'Сохраняем...' : ownReview ? 'Сохранить отзыв' : 'Оставить отзыв'}
                                </button>
                                {ownReview ? (
                                    <button className="ui-btn ui-btn--secondary ui-btn--block" type="button" onClick={() => handleDelete(ownReview)}>
                                        Удалить отзыв
                                    </button>
                                ) : null}
                            </form>
                        </div>
                    </div>
                </div>
            ) : null}

            {listError ? <div className="manga-inline-warning">{listError}</div> : null}

            <div className="reviews">
                {reviews.map((review) => (
                    <MangaReviewCard
                        key={review.id}
                        review={review}
                        canDelete={Boolean(isOwn(review)) || isModerator}
                        onDelete={handleDelete}
                    />
                ))}
            </div>
            {hasMore ? (
                <button type="button" className="ui-btn ui-btn--secondary ui-btn--block manga-reviews-more" onClick={() => loadPage(page + 1)}>
                    Загрузить ещё
                </button>
            ) : null}
        </div>
    );
};

MangaReviews.propTypes = {
    manga: PropTypes.object.isRequired,
    hasSession: PropTypes.bool,
    isModerator: PropTypes.bool,
    onRatingChange: PropTypes.func,
};

MangaReviews.defaultProps = {
    hasSession: false,
    isModerator: false,
    onRatingChange: () => {},
};

export default MangaReviews;
